import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { Box, IconButton, Typography } from '@mui/material';
import { memo, useCallback, useState, type DragEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { NavigationContextMenu } from '@/components/menu/NavigationContextMenu';
import { StatusChip } from '@/components/Shared/StatusChip';
import { navigationStudio } from '@/constants/navigationStudio';
import type {
  NavigationDropPosition,
  NavigationNode,
  NavigationNodeActionId,
} from '@/types/navigationConfig';
import {
  resolveNavigationIcon,
  resolveNodeLabel,
} from '@/utils/navigationDisplayUtils';
import { useNavigationPermission } from '@/utils/navigationPermissions';

interface NavigationNodeRowProps {
  node: NavigationNode;
  depth: number;
  isSelected: boolean;
  isExpanded: boolean;
  dropTargetId: string | null;
  dropPosition: NavigationDropPosition | null;
  onSelect: (nodeId: string) => void;
  onToggleExpand: (nodeId: string) => void;
  onMove: (
    dragId: string,
    targetId: string,
    position: NavigationDropPosition,
  ) => void;
  onDropTargetChange: (
    targetId: string | null,
    position: NavigationDropPosition | null,
  ) => void;
  onAction: (actionId: NavigationNodeActionId, node: NavigationNode) => void;
}

function mapVisibilityVariant(visibility: NavigationNode['visibility']) {
  if (visibility === 'visible') {
    return 'success';
  }

  if (visibility === 'hidden') {
    return 'warning';
  }

  return 'inactive';
}

export const NavigationNodeRow = memo(function NavigationNodeRow({
  node,
  depth,
  isSelected,
  isExpanded,
  dropTargetId,
  dropPosition,
  onSelect,
  onToggleExpand,
  onMove,
  onDropTargetChange,
  onAction,
}: NavigationNodeRowProps) {
  const { t } = useTranslation('entity-portal');
  const canReorder = useNavigationPermission('MENU_REORDER');
  const [isDragging, setIsDragging] = useState(false);
  const Icon = resolveNavigationIcon(node.icon);
  const hasChildren = node.children.length > 0;
  const isDropTarget = dropTargetId === node.id;
  const label = resolveNodeLabel(node.label, t);

  const handleDragStart = useCallback(
    (event: DragEvent<HTMLDivElement>) => {
      if (!canReorder) {
        event.preventDefault();
        return;
      }

      event.dataTransfer.setData('text/plain', node.id);
      event.dataTransfer.effectAllowed = 'move';
      setIsDragging(true);
    },
    [canReorder, node.id],
  );

  const handleDragEnd = useCallback(() => {
    setIsDragging(false);
    onDropTargetChange(null, null);
  }, [onDropTargetChange]);

  const resolveDropPosition = useCallback(
    (event: DragEvent<HTMLDivElement>): NavigationDropPosition => {
      const rect = event.currentTarget.getBoundingClientRect();
      const offsetY = event.clientY - rect.top;
      const relativeY = offsetY / rect.height;

      if (node.type === 'group' && relativeY > 0.35 && relativeY < 0.65) {
        return 'inside';
      }

      return relativeY < 0.5 ? 'before' : 'after';
    },
    [node.type],
  );

  const handleDragOver = useCallback(
    (event: DragEvent<HTMLDivElement>) => {
      if (!canReorder) {
        return;
      }

      event.preventDefault();
      event.dataTransfer.dropEffect = 'move';
      onDropTargetChange(node.id, resolveDropPosition(event));
    },
    [canReorder, node.id, onDropTargetChange, resolveDropPosition],
  );

  const handleDrop = useCallback(
    (event: DragEvent<HTMLDivElement>) => {
      event.preventDefault();

      if (!canReorder) {
        return;
      }

      const dragId = event.dataTransfer.getData('text/plain');

      if (!dragId || dragId === node.id) {
        onDropTargetChange(null, null);
        return;
      }

      const position = resolveDropPosition(event);
      onMove(dragId, node.id, position);
      onDropTargetChange(null, null);
    },
    [canReorder, node.id, onDropTargetChange, onMove, resolveDropPosition],
  );

  return (
    <Box
      draggable={canReorder}
      tabIndex={0}
      role="treeitem"
      aria-selected={isSelected}
      aria-expanded={hasChildren ? isExpanded : undefined}
      aria-grabbed={canReorder ? isDragging : undefined}
      onDragStart={handleDragStart}
      onDragEnd={handleDragEnd}
      onDragOver={handleDragOver}
      onDrop={handleDrop}
      onClick={() => onSelect(node.id)}
      onKeyDown={(event) => {
        if (event.key === 'Enter' || event.key === ' ') {
          event.preventDefault();
          onSelect(node.id);
          return;
        }

        if (hasChildren && event.key === 'ArrowRight' && !isExpanded) {
          event.preventDefault();
          onToggleExpand(node.id);
          return;
        }

        if (hasChildren && event.key === 'ArrowLeft' && isExpanded) {
          event.preventDefault();
          onToggleExpand(node.id);
        }
      }}
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 1,
        pl: `${12 + depth * navigationStudio.treeIndentPx}px`,
        pr: navigationStudio.treeRowPaddingX,
        minHeight: navigationStudio.treeRowMinHeight,
        cursor: 'pointer',
        opacity: isDragging ? 0.45 : 1,
        position: 'relative',
        outline: 'none',
        borderBottom: '1px solid',
        borderColor: navigationStudio.panelBorderColor,
        bgcolor: isSelected ? navigationStudio.selectedNodeBg : 'transparent',
        transition: 'background-color 0.15s ease',
        '&:hover': {
          bgcolor: isSelected
            ? navigationStudio.selectedNodeHoverBg
            : navigationStudio.nodeHoverBg,
        },
        '&:focus-visible': {
          boxShadow: (theme) => `inset 0 0 0 2px ${theme.palette.primary.main}`,
        },
        '&::before': isDropTarget
          ? {
              content: '""',
              position: 'absolute',
              left: 12,
              right: 12,
              height: 2,
              bgcolor: 'primary.main',
              zIndex: 1,
              ...(dropPosition === 'before' ? { top: 0 } : undefined),
              ...(dropPosition === 'after' ? { bottom: 0 } : undefined),
              ...(dropPosition === 'inside'
                ? {
                    top: 4,
                    bottom: 4,
                    height: 'auto',
                    border: 2,
                    borderColor: 'primary.main',
                    bgcolor: navigationStudio.nodeHoverBg,
                  }
                : undefined),
            }
          : undefined,
      }}
    >
      <DragIndicatorIcon
        sx={{
          color: 'text.disabled',
          cursor: canReorder ? 'grab' : 'default',
          fontSize: 18,
          flexShrink: 0,
        }}
      />

      {hasChildren ? (
        <IconButton
          size="small"
          aria-label={
            isExpanded
              ? t('navigationConfig.collapseNode', { node: label })
              : t('navigationConfig.expandNode', { node: label })
          }
          onClick={(event) => {
            event.stopPropagation();
            onToggleExpand(node.id);
          }}
          sx={{ p: 0.25, flexShrink: 0 }}
        >
          {isExpanded ? (
            <ExpandMoreIcon sx={{ fontSize: 18, color: 'text.secondary' }} />
          ) : (
            <ChevronRightIcon sx={{ fontSize: 18, color: 'text.secondary' }} />
          )}
        </IconButton>
      ) : (
        <Box sx={{ width: 28, flexShrink: 0 }} />
      )}

      <Icon
        sx={{
          fontSize: 18,
          flexShrink: 0,
          color: node.type === 'group' ? 'text.secondary' : 'primary.main',
        }}
      />

      <Typography
        variant={isSelected ? 'subtitle1' : 'subtitle2'}
        sx={{ color: 'text.primary', minWidth: 140, flex: '1 1 160px' }}
        noWrap
      >
        {label}
      </Typography>

      <Typography
        variant="mono"
        noWrap
        sx={{
          color: 'text.secondary',
          flex: '1 1 120px',
          display: { xs: 'none', sm: 'block' },
        }}
      >
        {node.route ?? '—'}
      </Typography>

      <Box sx={{ flexShrink: 0, display: { xs: 'none', md: 'flex' } }}>
        <StatusChip
          label={t(`navigationConfig.visibility.${node.visibility}`)}
          variant={mapVisibilityVariant(node.visibility)}
        />
      </Box>

      <Box
        sx={{ flexShrink: 0, ml: 'auto' }}
        onClick={(event) => event.stopPropagation()}
      >
        <NavigationContextMenu node={node} onAction={onAction} />
      </Box>
    </Box>
  );
});
